import { apiGet, apiPost } from './utils';
import type { PagedResponse } from '../types';

export interface Call {
  key: string;
  uuid: string;
  identity: string;
  associated_type: string;
  associated_uuid: string;
  number_to: string;
  number_from: string;
  twilio_call_sid: string;
  twilio_status: string;
  twilio_duration: number;
  twilio_price: string;
  date_created: string;
  date_updated: string;
  /** Placed from the legacy admin board (FHRequestPhoneCalls); no participants */
  is_legacy?: boolean;
}

export interface CallParticipant {
  key: string;
  uuid: string;
  call: string;
  phone_number: string;
  twilio_call_sid: string;
  twilio_status: string;
  twilio_label: string;
  twilio_muted: boolean;
  twilio_hold: boolean;
  twilio_duration: number;
  twilio_conference_sid: string;
  date_created: string;
  date_updated: string;
}

export interface TwilioToken {
  jwt: string;
}

export const callApi = {
  /**
   * Get Twilio JWT token for voice calls
   */
  async getToken(identityKey: string): Promise<TwilioToken> {
    return apiGet<TwilioToken>('/call/token', { identity: identityKey });
  },

  /**
   * Create a new call
   */
  async createCall(
    numberTo: string,
    associatedType: string,
    associatedUuid: string,
    identityKey: string
  ): Promise<Call> {
    return apiPost<Call>('/call/create', {
      number_to: numberTo,
      associated_type: associatedType,
      associated_uuid: associatedUuid,
      identity: identityKey,
    });
  },

  /**
   * Add a participant to an existing call
   */
  async addParticipant(
    numberTo: string,
    callKey: string,
    identityKey: string
  ): Promise<CallParticipant> {
    return apiPost<CallParticipant>('/call/add-participant', {
      number_to: numberTo,
      call: callKey,
      identity: identityKey,
    });
  },

  /**
   * Remove a participant from a call
   */
  async removeParticipant(
    callKey: string,
    callSid: string,
    identityKey: string
  ): Promise<{ status: string }> {
    return apiPost<{ status: string }>('/call/remove-participant', {
      call: callKey,
      twilio_call_sid: callSid,
      identity: identityKey,
    });
  },

  /**
   * Get participants for a call
   */
  async getParticipants(
    callKey: string,
    identityKey: string,
    cursor?: string
  ): Promise<PagedResponse<CallParticipant>> {
    return apiGet<PagedResponse<CallParticipant>>('/call/participants', {
      call: callKey,
      identity: identityKey,
      cursor,
    });
  },

  /**
   * Get calls for an associated resource (request or collab)
   */
  async getByAssociation(
    associatedType: string,
    associatedUuid: string,
    identityKey: string,
    cursor?: string
  ): Promise<PagedResponse<Call>> {
    return apiGet<PagedResponse<Call>>('/call/by-association', {
      associated_type: associatedType,
      associated_uuid: associatedUuid,
      identity: identityKey,
      cursor,
    });
  },
};
